import { FC, useEffect, useMemo, useState } from 'react';
import { CodeDisplayBox, CustomButton } from '@medtronic/pecc-react-component-library-js';
import { Button, ButtonGroup, Chip, Grid, TextField, Typography } from '@mui/material';
import { toString } from 'lodash';
import { CloseIcon } from '~/icons';
import connectionInstructionsConfig from '../config';
import { buttonDefaultStyle, buttonSelectedStyle, chipDefaultStyle, chipSelectedStyle } from '../styles';
import { ConnectionInstructionsType, ConnectionType, DEFAULT_PORT } from '../types';

const MIN_PORT = 0;
const MIN_PLACEHOLDER_PORT = 3000;
const MAX_PORT = 65535;
const buttonsConfig = [ConnectionType.SSM_CONNECTION, ConnectionType.PORT];

interface StartSessionCommandsProps {
  type: ConnectionInstructionsType;
  selectedPort: number | null;
  onConnectionTypeChange: (connectionType: ConnectionType) => void;
  onPortChange: (port: number | null) => void;
  startCommand: string;
}

const StartSessionCommands: FC<StartSessionCommandsProps> = ({
  type,
  selectedPort,
  onPortChange,
  onConnectionTypeChange,
  startCommand,
}) => {
  const [selectedButton, setSelectedButton] = useState<ConnectionType>(ConnectionType.SSM_CONNECTION);

  const [port, setPort] = useState<number>(NaN);
  const [ports, setPorts] = useState<Set<number>>(new Set([selectedPort ?? DEFAULT_PORT]));

  useEffect(() => {
    onConnectionTypeChange(selectedButton);
  }, [selectedButton, onConnectionTypeChange]);

  const handleAddPort = () => {
    const newPorts = new Set(ports);
    newPorts.add(port);
    setPort(NaN);
    setPorts(newPorts);
    onPortChange(port);
  };

  const handleDelete = (port: number) => {
    const newPorts = new Set(ports);
    newPorts.delete(port);
    setPorts(newPorts);
  };

  const handlePortClick = (port: number) => {
    onPortChange(port);
  };

  const isPortInvalid = useMemo(() => ports.has(port) || port < MIN_PORT || port > MAX_PORT, [port, ports]);

  const placeholderPort = useMemo(() => {
    for (let port = MIN_PLACEHOLDER_PORT; port < MAX_PORT; port++) {
      if (!ports.has(port)) {
        return `${port}`;
      }
    }
    // Unlikely, but if nothing found in range, fallback to <MIN_DEFAULT_PORT
    for (let port = 1; port < MIN_PLACEHOLDER_PORT; port++) {
      if (!ports.has(port)) {
        return `${port}`;
      }
    }

    return 'All ports are taken'; // Unlikely case where all ports are taken
  }, [ports]);

  const getErrorMessage = () => {
    if (ports.has(port)) {
      return `${port} port already exists`;
    } else {
      return `${port} is an invalid port`;
    }
  };

  return (
    <>
      {connectionInstructionsConfig[type].startSessionTitle}
      {connectionInstructionsConfig[type].showPortTab && (
        <Grid size={12} container spacing={2} alignItems="center">
          <Typography>TYPE:</Typography>
          <ButtonGroup disableElevation variant="contained" aria-label="Disabled button group">
            {buttonsConfig.map((button) => (
              <Button
                variant={selectedButton === button ? 'contained' : 'outlined'}
                sx={selectedButton === button ? buttonSelectedStyle : buttonDefaultStyle}
                onClick={() => setSelectedButton(button)}
              >
                {button}
              </Button>
            ))}
          </ButtonGroup>
        </Grid>
      )}
      {selectedButton === ConnectionType.PORT && (
        <>
          <Grid size={12} container mb={2} spacing={2} alignItems="center">
            <TextField
              fullWidth={false}
              error={isPortInvalid}
              helperText={isPortInvalid ? getErrorMessage() : ''}
              value={toString(port)}
              onChange={(e) => setPort(parseInt(e.target.value))}
              placeholder={placeholderPort}
              label="PORT"
              type="number"
            />
            <CustomButton label="Add" disabled={isPortInvalid || (port !== 0 && !port)} onClick={handleAddPort} />
          </Grid>
          <Grid size={12} container spacing={1} alignItems="center">
            {Array.from(ports).map((port) => (
              <Chip
                key={port}
                sx={selectedPort === port ? chipSelectedStyle : chipDefaultStyle}
                label={
                  <Typography
                    variant="body1"
                    color={selectedPort === port ? 'textWhiteText.main' : 'textBlackReduced.main'}
                  >
                    {port}
                  </Typography>
                }
                onClick={() => handlePortClick(port)}
                onDelete={() => handleDelete(port)}
                deleteIcon={<CloseIcon style={{ color: selectedPort === port ? 'white' : 'black' }} />}
              />
            ))}
          </Grid>
        </>
      )}
      <Grid size={12}>
        <CodeDisplayBox darkMode code={startCommand} />
      </Grid>
    </>
  );
};

export default StartSessionCommands;
